import { useEffect, type RefObject } from 'react'

/**
 * Skip link component for keyboard navigation
 * Allows screen reader and keyboard users to skip to main content
 */
export function SkipLink() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-sky-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400"
    >
      Skip to main content
    </a>
  )
}

/**
 * Focus trap hook for modals and dialogs
 * Keeps focus within the referenced element when using Tab/Shift+Tab
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, isActive = true) {
  useEffect(() => {
    if (!isActive) return

    const element = ref.current
    if (!element) return

    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ')

    const focusableElements = element.querySelectorAll<HTMLElement>(focusableSelectors)
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement?.focus()
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement?.focus()
      }
    }

    element.addEventListener('keydown', handleKeyDown)
    firstElement?.focus()

    return () => element.removeEventListener('keydown', handleKeyDown)
  }, [ref, isActive])
}

/**
 * Announce messages to screen readers using a live region
 */
export function useAnnounce() {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const liveRegion = document.createElement('div')
    liveRegion.setAttribute('aria-live', priority)
    liveRegion.setAttribute('aria-atomic', 'true')
    liveRegion.setAttribute('class', 'sr-only')
    liveRegion.textContent = message
    document.body.appendChild(liveRegion)

    setTimeout(() => {
      document.body.removeChild(liveRegion)
    }, 1000)
  }

  return announce
}

/**
 * Hook to manage focus when navigating between pages
 */
export function usePageFocus(title: string) {
  useEffect(() => {
    // Update document title
    document.title = `${title} | KCS Dashboard`

    // Focus on main content for screen readers
    const mainContent = document.getElementById('main-content')
    if (mainContent) {
      mainContent.focus()
    }
  }, [title])
}
